import type { QuoteJobAddress } from 'paintscout'

const normalize = (val?: string | null) => (val || '').trim().toLowerCase()

/**
 * True when an address has nothing in any of the four fields we compare — covers null/undefined,
 * `{}`, and all-empty-strings (which is what `DealForm` submits for an address-less deal).
 */
export function isEmptyAddress(address?: QuoteJobAddress | null): boolean {
  return !address?.address && !address?.city && !address?.province && !address?.postal
}

function addressesDiffer(a?: QuoteJobAddress | null, b?: QuoteJobAddress | null): boolean {
  return (
    normalize(a?.address) !== normalize(b?.address) ||
    normalize(a?.city) !== normalize(b?.city) ||
    normalize(a?.province) !== normalize(b?.province) ||
    normalize(a?.postal) !== normalize(b?.postal)
  )
}

/**
 * Compares a quote/invoice job address against a deal's location and returns true if they
 * meaningfully differ. Used to decide whether to warn the user (and offer to sync the two)
 * when linking a quote to a deal.
 *
 * Returns false (no mismatch) when either side has no address at all — there's nothing to
 * compare against, so we don't want to nag. Comparison is case-insensitive and trims
 * surrounding whitespace so cosmetic differences don't count as a mismatch.
 *
 * For the "user just set an address, push it to the other side" case — where a blank other side is
 * the whole point — use {@link hasAddressToSync} instead.
 */
export function hasAddressMismatch(
  quoteAddress?: QuoteJobAddress | null,
  dealAddress?: QuoteJobAddress | null
): boolean {
  // If either side has no address, there's nothing to mismatch against
  if (isEmptyAddress(dealAddress) || isEmptyAddress(quoteAddress)) {
    return false
  }

  return addressesDiffer(quoteAddress, dealAddress)
}

/**
 * Directional counterpart to {@link hasAddressMismatch}: the user just set `sourceAddress` on one
 * document — does `targetAddress` need it?
 *
 * Unlike `hasAddressMismatch`, a blank target counts as needing the address. That's the whole point
 * of #22064: an estimate/invoice and its linked deal that both start out address-less never
 * prompted, so adding an address to one of them silently left the other side — and the scheduled
 * job hanging off the deal — blank.
 *
 * A blank *source* never prompts: clearing an address shouldn't offer to blank everything else out.
 * That mirrors the server-side gate this feeds (`hasAddressChanged`, used by `putDeal`), which also
 * requires the new address to be non-empty before it writes anything.
 */
export function hasAddressToSync(
  sourceAddress?: QuoteJobAddress | null,
  targetAddress?: QuoteJobAddress | null
): boolean {
  return !isEmptyAddress(sourceAddress) && addressesDiffer(sourceAddress, targetAddress)
}
